import {
  FileText,
  LayoutDashboard,
  ClipboardCheck,
  History,
  LogOut,
  Menu,
  X,
  Shield,
  ChevronLeft,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { logout, getUser } from '../utils/auth';

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();

  const isApprover = user?.roles?.some(role =>
    ['pic', 'area_manager', 'she', 'management'].includes(role)
  );
  const isAdmin = user?.roles?.some(role =>
    ['she', 'management', 'admin'].includes(role)
  );

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Shield, label: 'Admin Dashboard', path: '/admin-dashboard', showIf: isAdmin },
    { icon: FileText, label: 'My Requests', path: '/my-requests', roles: ['vendor'] },
    { icon: ClipboardCheck, label: 'My Approvals', path: '/my-approvals', showIf: isApprover },
    { icon: History, label: 'History', path: '/history' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isOpen && !e.target.closest('.sidebar-container') && !e.target.closest('.sidebar-toggle')) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  const SidebarContent = () => (
    <div
      className={`h-full flex flex-col text-[#FAF5EB] transition-all duration-300 ${
        isCollapsed ? 'w-16' : 'w-64'
      }`}
      style={{ backgroundColor: '#0D3B30' }}
    >
      {/* Header */}
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        {!isCollapsed && (
          <div>
            <h1 className="text-lg font-bold tracking-wide">
              Digital Work Permit
            </h1>
            <p className="text-xs text-white/70">
              PT. Flora Food Manufacturing Indonesia
            </p>
          </div>
        )}

        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-2 hover:bg-white/10 rounded-lg transition hidden lg:block"
          title={isCollapsed ? 'Expand' : 'Collapse'}
        >
          <ChevronLeft
            className={`w-5 h-5 transition-transform ${
              isCollapsed ? 'rotate-180' : ''
            }`}
          />
        </button>

        <button
          onClick={() => setIsOpen(false)}
          className="p-2 hover:bg-white/10 rounded-lg transition lg:hidden"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* User Info */}
      <div className={`p-4 border-b border-white/10 ${isCollapsed ? 'px-2' : ''}`}>
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'}`}>
          <div className="w-10 h-10 bg-[#FAF5EB] text-[#0D3B30] rounded-full flex items-center justify-center font-semibold">
            {user?.name?.charAt(0).toUpperCase()}
          </div>

          {!isCollapsed && (
            <div className="overflow-hidden">
              <p className="text-sm font-medium truncate">
                {user?.name}
              </p>
              <p className="text-xs text-white/70 truncate">
                {user?.roles?.join(', ').toUpperCase()}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-2 overflow-y-auto">
        {menuItems.map((item) => {
          if (item.showIf !== undefined && !item.showIf) return null;
          if (item.roles && !item.roles.some(role => user?.roles?.includes(role))) return null;

          const isActive = location.pathname === item.path;
          const Icon = item.icon;

          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center ${
                isCollapsed ? 'justify-center' : 'gap-3'
              } px-3 py-2.5 rounded-lg mb-1 transition ${
                isActive
                  ? 'bg-[#FAF5EB] text-[#0D3B30] font-semibold'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
              title={isCollapsed ? item.label : ''}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {!isCollapsed && <span className="text-sm">{item.label}</span>}
            </button>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="p-2 border-t border-white/10">
        <button
          onClick={handleLogout}
          className={`w-full flex items-center ${
            isCollapsed ? 'justify-center' : 'gap-3'
          } px-3 py-2.5 rounded-lg text-white/80 hover:bg-red-600 hover:text-white transition`}
          title={isCollapsed ? 'Logout' : ''}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {!isCollapsed && <span className="text-sm">Keluar</span>}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile Toggle */}
      <button
        onClick={() => setIsOpen(true)}
        className="sidebar-toggle fixed top-4 left-4 z-40 lg:hidden p-2 rounded-lg shadow-lg"
        style={{ backgroundColor: '#0D3B30', color: '#FAF5EB' }}
      >
        <Menu className="w-6 h-6" />
      </button>

      {/* Desktop Sidebar */}
      <aside
        className={`sidebar-container hidden lg:block fixed left-0 top-0 h-full z-30 transition-all duration-300 ${
          isCollapsed ? 'w-16' : 'w-64'
        }`}
      >
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      {isOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setIsOpen(false)} />
          <aside className="sidebar-container absolute left-0 top-0 h-full shadow-xl">
            <SidebarContent />
          </aside>
        </div>
      )}
    </>
  );
};

export default Sidebar;
